import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { User } from '../models/user';
import { SessionUser } from '../models/session';

/**
 * Selected users for the current search or picker context (e.g. filter chips, invite flow,
 * event attendees). The map is keyed by Mongo `_id` when present, otherwise by `email` for
 * email-only entries. The map lives in a `BehaviorSubject`.
 *
 * Provide on the host component (`providers: [UserSearchSelectionService]`) so each UI is isolated.
 */
@Injectable()
export class UserSearchSelectionService {
  /**
   * Holds all users that should be **excluded** from search dropdown results when this service is
   * bound to a user-search component.
   *
   * The map contains two categories of users:
   *  - Users who have **already been acted on** (e.g. already invited to a space, already added to
   *    an event attendees list) and therefore must not appear as suggestions again.
   *  - Users who are **pending selection** in the current session (e.g. picked in the invite form
   *    but not yet sent) and should likewise be hidden to prevent duplicates.
   *
   * Example — Share Session dialog:
   *   Populated with both the users who already have a pending/accepted invite to the space AND
   *   the users the host has just selected in the current invite flow. This ensures the dropdown
   *   never surfaces someone who is already in or being added to the space.
   *
   * Example — Event attendees (organizers + participants):
   *   Because `EventPropertiesComponent` provides this service at its own level, the single
   *   instance is shared across both the hosts `EventAttendeesComponent` and the participants
   *   `EventAttendeesComponent`. Selecting a user in one section automatically excludes them from
   *   the search in the other section, preventing the same person from being added to both roles.
   */
  private readonly selectedUsersMapSubject = new BehaviorSubject<Map<string, User | SessionUser>>(
    new Map(),
  );

  /**
   * Emits the selected users as an array whenever the underlying map updates.
   */
  readonly selectedUsers$ = this.selectedUsersMapSubject.pipe(
    map((userMap) => [...userMap.values()]),
  );

  /**
   * Snapshot of selected users.
   * Prefer `selectedUsers$` in templates; use this for imperative code after mutations.
   */
  get selectedUsers(): (User | SessionUser)[] {
    return [...this.selectedUsersMap.values()];
  }

  /**
   * Mongo `_id` values for selected entries that have one (email-only chips are omitted).
   */
  get selectedUserIds(): string[] {
    return [...this.selectedUsersMap.values()].map((u) => u._id).filter(Boolean);
  }

  /**
   * Replaces the map with exactly the given users (by `_id` or `email` key).
   *
   * @param users Users to store; defaults to an empty array.
   */
  initializeSelectedUsers(users: (User | SessionUser)[] = []): void {
    this.selectedUsersMapSubject.next(new Map());
    this.addSelectedUsers(users);
  }

  /**
   * Adds or updates users in the map, keyed by `_id` or `email`.
   *
   * @param users Users to add; entries without a usable `_id` or `email` are ignored.
   */
  addSelectedUsers(users: (User | SessionUser)[]): void {
    const next = new Map(this.selectedUsersMap);
    users.forEach((user) => {
      const entryKey = this.resolveEntryKey(user);
      if (entryKey) {
        next.set(entryKey, user);
      }
    });
    this.selectedUsersMapSubject.next(next);
  }

  /**
   * Removes users from the map by `_id` or `email` key.
   *
   * @param users Users to remove; entries without a matching key are ignored.
   */
  removeSelectedUsers(users: (User | SessionUser)[]): void {
    const next = new Map(this.selectedUsersMap);
    users.forEach((user) => {
      const entryKey = this.resolveEntryKey(user);
      if (entryKey) {
        next.delete(entryKey);
      }
    });
    this.selectedUsersMapSubject.next(next);
  }

  /**
   * Checks whether a user is in the current selection by resolving its `_id` or `email` key.
   *
   * @param user The user object to check.
   * @returns `true` if the resolved key is stored, `false` otherwise.
   */
  isSelected(user: User | SessionUser): boolean {
    const key = this.resolveEntryKey(user);
    return key ? this.selectedUsersMap.has(key) : false;
  }

  /** Current selected users map held by {@link selectedUsersMapSubject}. */
  private get selectedUsersMap(): Map<string, User | SessionUser> {
    return this.selectedUsersMapSubject.getValue();
  }

  private resolveEntryKey(user: User | SessionUser): string | undefined {
    if (user._id) {
      return user._id;
    } else if ('email' in user) {
      return user.email;
    }
    return undefined;
  }
}
